import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BookOpen, LogOut, User, Menu, X } from 'lucide-react';

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const AUTH_ROUTES = ['/nmdf-direction-k7x2', '/nmdf-enseignant-m4p9', '/connexion', '/inscription'];
  const isAuthPage = AUTH_ROUTES.some(r => location.pathname === r);

  // Hide on login/signup pages and for roles that have their own dashboard nav.
  if (isAuthPage || user?.role === 'admin' || user?.role === 'formateur') return null;

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
    navigate('/');
  };

  const nomAffiche = user
    ? (user.role === 'admin' ? 'Admin' : `${user.prenom || ''}`.trim())
    : null;

  const dashboardUrl = user?.role === 'apprenant'
    ? '/dashboard'
    : '/'; // Phase 1: non-apprenant → accueil
  /* Phase 2: restore formateur/admin dashboard links
    : user?.role === 'formateur'
    ? '/nmdf-enseignant-m4p9'
    : user?.role === 'admin'
    ? '/nmdf-direction-k7x2'
    : '/connexion'; */

  const navLinks = [
    { to: '/catalogue', label: 'Catalogue' },
    { to: '/valeurs', label: 'Valeurs' },
    { to: '/qualiopi', label: 'Qualiopi' },
    { to: '/temoignages', label: 'Témoignages' },
    { to: '/blog', label: 'Blog' },
    { to: '/faq', label: 'FAQ' },
    { to: '/apropos', label: 'À propos' },
    { to: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className="sticky top-0 z-50"
      style={{
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderBottom: '1px solid #e2e8f0',
        boxShadow: '0 1px 12px rgba(0,0,0,0.06)',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">

        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 flex-shrink-0">
          <img src="/logo-sm.webp" alt="Nomedif" className="h-10 w-10 object-contain" />
          <div>
            <div className="text-slate-900 font-black text-lg leading-none">Nomedif</div>
            <div className="text-blue-600 text-xs font-semibold">Formations</div>
          </div>
        </Link>

        {/* Nav desktop */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className="relative px-3 py-2 text-sm font-semibold rounded-lg transition-colors"
              style={{
                color: isActive(to) ? '#1d4ed8' : '#475569',
                background: isActive(to) ? 'rgba(37,99,235,0.07)' : 'transparent',
              }}
            >
              {label}
              {isActive(to) && (
                <span
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full"
                  style={{ background: '#1d4ed8' }}
                />
              )}
            </Link>
          ))}
        </nav>

        {/* Boutons desktop */}
        <div className="hidden lg:flex gap-2 items-center">
          {user ? (
            <>
              <Link
                to={dashboardUrl}
                className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-xl transition-all hover:bg-blue-50"
                style={{ color: '#1d4ed8', border: '1px solid #bfdbfe' }}
              >
                <User size={15} />
                {nomAffiche}
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all hover:opacity-90"
                style={{ background: '#ef4444' }}
              >
                <LogOut size={15} />
                Déconnexion
              </button>
            </>
          ) : (
            <>
              <Link
                to="/inscription"
                className="text-sm font-semibold px-4 py-2 rounded-xl transition-all hover:bg-slate-50"
                style={{ color: '#475569', border: '1px solid #e2e8f0' }}
              >
                Inscription
              </Link>
              <Link
                to="/connexion"
                className="flex items-center gap-2 text-white px-5 py-2 rounded-xl text-sm font-bold transition-all hover:opacity-90"
                style={{ background: 'linear-gradient(90deg, #1d4ed8, #2563eb)' }}
              >
                <BookOpen size={15} />
                Connexion
              </Link>
            </>
          )}
        </div>

        {/* Burger mobile */}
        <button
          className="lg:hidden p-2 rounded-xl transition-colors hover:bg-slate-100"
          style={{ color: '#475569' }}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Menu mobile */}
      {menuOpen && (
        <div
          className="lg:hidden px-4 py-5 flex flex-col gap-1"
          style={{ background: 'white', borderTop: '1px solid #f1f5f9' }}
        >
          {navLinks.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              onClick={() => setMenuOpen(false)}
              className="px-4 py-3 rounded-xl text-sm font-semibold transition-colors"
              style={{
                color: isActive(to) ? '#1d4ed8' : '#475569',
                background: isActive(to) ? 'rgba(37,99,235,0.07)' : 'transparent',
              }}
            >
              {label}
            </Link>
          ))}
          <div className="pt-3 border-t border-gray-100 mt-2 space-y-2">
            {user ? (
              <>
                <Link
                  to={dashboardUrl}
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2 w-full px-4 py-3 rounded-xl text-sm font-semibold"
                  style={{ color: '#1d4ed8', background: 'rgba(37,99,235,0.07)' }}
                >
                  <User size={16} /> {nomAffiche}
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 w-full text-white px-4 py-3 rounded-xl font-bold text-sm"
                  style={{ background: '#ef4444' }}
                >
                  <LogOut size={16} /> Déconnexion
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/inscription"
                  onClick={() => setMenuOpen(false)}
                  className="block text-center px-4 py-3 rounded-xl text-sm font-semibold"
                  style={{ color: '#475569', border: '1px solid #e2e8f0' }}
                >
                  Inscription
                </Link>
                <Link
                  to="/connexion"
                  onClick={() => setMenuOpen(false)}
                  className="block text-center text-white px-4 py-3 rounded-xl font-bold text-sm"
                  style={{ background: 'linear-gradient(90deg, #1d4ed8, #2563eb)' }}
                >
                  Connexion
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;
